/**
 * Хук для обработки swipe жестов на мобильных
 */

import { useEffect, useRef } from 'react';

/**
 * Направления swipe
 */
export type SwipeDirection = 'up' | 'down' | 'left' | 'right';

/**
 * Опции для useSwipeGesture
 */
interface SwipeOptions {
  /** Минимальная дистанция для swipe (px) */
  minDistance?: number;
  /** Максимальное время для swipe (ms) */
  maxDuration?: number;
  /** Колбек при swipe */
  onSwipe?: (direction: SwipeDirection) => void;
  /** Колбек при swipe down */
  onSwipeDown?: () => void;
  /** Колбек при swipe up */
  onSwipeUp?: () => void;
  /** Колбек при swipe left */
  onSwipeLeft?: () => void;
  /** Колбек при swipe right */
  onSwipeRight?: () => void;
}

/**
 * Хук для обработки swipe жестов
 * 
 * @description Отслеживает touch события и определяет направление swipe
 */
export function useSwipeGesture(
  elementRef: React.RefObject<HTMLElement>,
  options: SwipeOptions = {}
) {
  const {
    minDistance = 50,
    maxDuration = 500,
    onSwipe,
    onSwipeDown,
    onSwipeUp,
    onSwipeLeft,
    onSwipeRight,
  } = options;

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const duration = Date.now() - touchStartRef.current.time;

      touchStartRef.current = null;

      // Проверяем условия для swipe
      if (duration > maxDuration) return;

      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX < minDistance && absY < minDistance) return;

      // Определяем направление (преобладающее)
      let direction: SwipeDirection;

      if (absX > absY) {
        // Горизонтальный swipe
        direction = deltaX > 0 ? 'right' : 'left';
      } else {
        // Вертикальный swipe
        direction = deltaY > 0 ? 'down' : 'up';
      }

      // Вызываем колбеки
      onSwipe?.(direction);

      switch (direction) {
        case 'down':
          onSwipeDown?.();
          break;
        case 'up':
          onSwipeUp?.();
          break;
        case 'left':
          onSwipeLeft?.();
          break;
        case 'right':
          onSwipeRight?.();
          break;
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    elementRef,
    minDistance,
    maxDuration,
    onSwipe,
    onSwipeDown,
    onSwipeUp,
    onSwipeLeft,
    onSwipeRight,
  ]);
}

